import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Heart, Flame, Check, Clock, CheckCircle2, HelpCircle, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import PullToRefresh from '../../components/PullToRefresh'
import Modal from '../../components/ui/Modal'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess, getPartnerId, getDisplayName } from '../../lib/owner'
import Avatar from '../../components/Avatar'
import { supabase } from '../../lib/supabaseClient'
import { fireWinCelebration } from '../../lib/celebration'
import { cn } from '../../lib/utils'
import {
  pickDailyQuestion,
  findQuestion,
  findChoice,
  localDateKey,
} from '../../components/games/questions/logic'
import {
  useMyAnswer,
  usePartnerAnswer,
  useJointAnswerStreak,
  useQuestionPool,
  useRecentDailyQuestionIds,
} from '../../components/games/questions/hooks'
import { CATEGORIES } from '../../components/games/questions/bank'
import { X } from 'lucide-react'

const HOW_TO_SEEN_KEY = 'phoenix:questions-howto-seen'

export function QuestionsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const todayKey = useMemo(() => localDateKey(), [])
  // 30-day exclusion window for the daily — both partners derive the
  // same set from the shared couples_question_answers table, so the
  // pick stays deterministic (same exclusion + same date → same Q).
  const recentDailyIds = useRecentDailyQuestionIds(30)
  const baseQuestion = useMemo(() => pickDailyQuestion(new Date(), recentDailyIds), [recentDailyIds])

  const partnerId = useMemo(() => getPartnerId(user), [user])
  const partnerName = useMemo(() => getDisplayName(partnerId, locale), [partnerId, locale])

  const myAnswer = useMyAnswer(user?.id, todayKey)
  const partnerAnswer = usePartnerAnswer(partnerId, todayKey)
  // Pool drives the suggestion modal here; the daily picker itself
  // stays on BANK only (its day-hash math depends on a stable size).
  const { pool, submit: submitSuggestion } = useQuestionPool(user?.id)
  const [suggestOpen, setSuggestOpen] = useState(false)

  // If either partner already answered today, lock onto THEIR
  // question id and override the picker. Without this, a mid-day
  // content rotation (we push a few new questions, or any reorder
  // that shifts the date-hash modulo result) leaves their saved
  // answers stranded: the UI displays the new pick, the options
  // are disabled because iAnswered=true, and the reveal pairs the
  // old answer against the wrong question.
  //
  // Either partner having answered guarantees a question they
  // intended; mine takes priority over the partner's so a stale
  // partner row never overrides my live state. Falls back to the
  // picker only when neither row exists.
  const lockedQuestionId = myAnswer.row?.question_id || partnerAnswer.row?.question_id || null
  const question = useMemo(() => {
    if (!lockedQuestionId) return baseQuestion
    return findQuestion(lockedQuestionId) || baseQuestion
  }, [lockedQuestionId, baseQuestion])

  // Joint streak — recompute whenever either side flips into the
  // "answered today" state.
  const refreshKey = `${myAnswer.row?.choice_id || ''}|${partnerAnswer.row?.choice_id || ''}`
  const jointStreak = useJointAnswerStreak(user?.id, partnerId, refreshKey)

  const [howToOpen, setHowToOpen] = useState(false)
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(HOW_TO_SEEN_KEY)
      if (!seen) setHowToOpen(true)
    } catch { /* private mode — no first-visit prompt is fine */ }
  }, [])
  const dismissHowTo = () => {
    setHowToOpen(false)
    try { window.localStorage.setItem(HOW_TO_SEEN_KEY, '1') } catch { /* noop */ }
  }

  const myChoiceId = myAnswer.row?.choice_id || null
  const iAnswered = !!myChoiceId
  const partnerAnswered = !!partnerAnswer.row?.choice_id
  const bothAnswered = iAnswered && partnerAnswered

  // Confetti when the BOTH-answered state fires for the first time
  // — that's the satisfying moment in this game. Skip on first paint
  // if we both already had rows from earlier today (the page just
  // hydrating shouldn't replay celebration).
  const [hydratedAtBoth, setHydratedAtBoth] = useState(() => null)
  useEffect(() => {
    if (myAnswer.loading || partnerAnswer.loading) return
    if (hydratedAtBoth === null) {
      setHydratedAtBoth(bothAnswered)
      return
    }
    if (!hydratedAtBoth && bothAnswered) {
      fireWinCelebration()
      setHydratedAtBoth(true)
    }
  }, [bothAnswered, myAnswer.loading, partnerAnswer.loading, hydratedAtBoth])

  const submitChoice = useCallback(async (choiceId) => {
    if (!user || iAnswered) return
    const payload = {
      user_id: user.id,
      day: todayKey,
      question_id: question.id,
      choice_id: choiceId,
    }
    // Optimistic — update the hook's row before the network round-trip
    // so the UI flips to "waiting on partner" instantly.
    myAnswer.setRow({ ...payload, created_at: new Date().toISOString() })
    const { error } = await supabase
      .from('couples_question_answers')
      .insert(payload)
    if (error) {
      // Most likely cause: UNIQUE(user_id, day) — they already answered
      // somehow (multi-tab, stale state). Re-fetch and let the UI sort it.
      const { data } = await supabase
        .from('couples_question_answers')
        .select('question_id, choice_id, created_at')
        .eq('user_id', user.id)
        .eq('day', todayKey)
        .maybeSingle()
      myAnswer.setRow(data || null)
      return
    }
    // Fire-and-forget push to the partner. Never blocks the UI.
    supabase.functions
      .invoke('couples-question-partner-push', {
        body: { day: todayKey, question_id: question.id },
      })
      .catch(() => { /* push is best-effort */ })
  }, [user, iAnswered, todayKey, question, myAnswer])

  const goBack = () => navigate('/')
  const handleRefresh = () => Promise.resolve()

  // Localized text helpers — the bank is bilingual; choose the side
  // matching the current UI locale.
  const localText = (obj) => (obj?.[locale] || obj?.en || '')

  // Partner row chrome: status icon + label + maybe choice text.
  const partnerStatusIcon = partnerAnswered ? CheckCircle2 : Clock
  const partnerStatusTone = partnerAnswered ? 'text-emerald-500' : 'text-muted-foreground'

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
            {t('games.questions.title')}
          </h1>
          {jointStreak > 0 && (
            <span
              className="inline-flex items-center gap-1 text-sm font-bold text-app-primary-500"
              dir="ltr"
              title={t('games.questions.streakLabel')}
            >
              <Heart className="w-3.5 h-3.5 fill-current" />
              <Flame className="w-3.5 h-3.5" />
              {jointStreak}
            </span>
          )}
          <button
            type="button"
            onClick={() => setHowToOpen(true)}
            aria-label={t('games.questions.howToPlay')}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Question card */}
        <Card className="border-app-primary-500/30 bg-app-primary-500/5">
          <CardContent className="p-3 sm:p-4 space-y-3">
            <p className="text-base font-semibold text-foreground" dir="auto">
              {localText(question.text)}
            </p>
            <div className="space-y-2">
              {question.options.map((opt) => {
                const selected = myChoiceId === opt.id
                const disabled = iAnswered && !selected
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => submitChoice(opt.id)}
                    disabled={iAnswered}
                    className={cn(
                      'w-full text-start p-3 rounded-md border-2 transition-colors',
                      selected
                        ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500 font-semibold'
                        : 'border-border text-foreground hover:bg-accent',
                      disabled && 'opacity-50 hover:bg-transparent',
                    )}
                    dir="auto"
                  >
                    <span className="flex items-center gap-2">
                      {selected && <Check className="w-4 h-4 flex-shrink-0" />}
                      <span className="flex-1">{localText(opt.text)}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Partner row + reveal. Renders only when this user is in the
            trusted pair; solo users get the question without the
            partner half (still usable as a journal). */}
        {partnerId && partnerName && (
          <Card>
            <CardContent className="p-3 sm:p-4 space-y-2">
              {!bothAnswered ? (
                <div className="flex items-center gap-2 text-sm" dir="auto">
                  {React.createElement(partnerStatusIcon, {
                    className: cn('w-4 h-4 flex-shrink-0', partnerStatusTone),
                  })}
                  <span className="text-foreground">
                    {partnerAnswered
                      ? t('games.questions.partnerAnsweredWaiting', { name: partnerName })
                      : iAnswered
                        ? t('games.questions.partnerNotYet', { name: partnerName })
                        : t('games.questions.partnerNotStarted', { name: partnerName })}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500" dir="auto">
                    {t('games.questions.reveal')}
                  </p>
                  <RevealRow
                    userId={user?.id}
                    label={t('games.questions.you')}
                    choice={localText(findChoice(question, myChoiceId)?.text)}
                  />
                  <RevealRow
                    userId={partnerId}
                    label={partnerName}
                    choice={localText(findChoice(
                      findQuestion(partnerAnswer.row?.question_id),
                      partnerAnswer.row?.choice_id,
                    )?.text)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>

      {howToOpen && <HowToPlayDialog onClose={dismissHowTo} t={t} />}
      {suggestOpen && (
        <SuggestionDialog
          t={t}
          onClose={() => setSuggestOpen(false)}
          onSubmit={submitSuggestion}
        />
      )}
    </PullToRefresh>
  )
}

function RevealRow({ userId, label, choice }) {
  // Block-level flex so each row sits on its own line — inline-flex
  // was collapsing the two rows onto one line in RTL. Avatar inline
  // before the label because the label is standalone (not a
  // sentence). The label + choice flow together so the eye reads
  // "إنت: آيفون" as a single unit.
  return (
    <div className="text-sm text-foreground flex items-center gap-1.5" dir="auto">
      <Avatar userId={userId} size={16} />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium flex-1 min-w-0 truncate">{choice || '—'}</span>
    </div>
  )
}

// Suggest-a-question form. Hand-rolled inside QuestionsPage because
// it's the only place that uses it (the Quiz reads suggestions via
// the same pool but doesn't author them).
//
// Fields the proposer fills:
//   - category (one of CATEGORIES, dropdown)
//   - text_en + text_ar (single-line inputs)
//   - 3-5 options, each with text_en + text_ar; options keep a
//     locally-generated id (`opt-${i}`) since they only need to be
//     unique within their question's row.
// All four "required" gates (category, EN question, AR question, at
// least 3 options each with both EN and AR text) light up the
// Submit button.
function SuggestionDialog({ t, onClose, onSubmit }) {
  const [category, setCategory] = useState(CATEGORIES[0])
  const [textEn, setTextEn] = useState('')
  const [textAr, setTextAr] = useState('')
  const [options, setOptions] = useState([
    { en: '', ar: '' },
    { en: '', ar: '' },
    { en: '', ar: '' },
  ])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const setOpt = (i, field, value) => {
    setOptions((curr) => curr.map((o, idx) => (idx === i ? { ...o, [field]: value } : o)))
  }
  const addOption = () => {
    if (options.length >= 5) return
    setOptions((curr) => [...curr, { en: '', ar: '' }])
  }
  const removeOption = (i) => {
    if (options.length <= 3) return
    setOptions((curr) => curr.filter((_, idx) => idx !== i))
  }

  const optionsValid = options.length >= 3
    && options.every((o) => o.en.trim().length > 0 && o.ar.trim().length > 0)
  const canSubmit = !submitting
    && category
    && textEn.trim().length > 0
    && textAr.trim().length > 0
    && optionsValid

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    const payload = {
      category,
      textEn: textEn.trim(),
      textAr: textAr.trim(),
      options: options.map((o, i) => ({
        id: `opt-${i + 1}`,
        text: { en: o.en.trim(), ar: o.ar.trim() },
      })),
    }
    const { error: submitError } = await onSubmit(payload)
    setSubmitting(false)
    if (submitError) {
      setError(t('games.questions.suggest.submitFailed'))
      return
    }
    onClose()
  }

  return (
    <Modal
      open
      onClose={submitting ? undefined : onClose}
      closeOnScrim={!submitting}
      closeOnEsc={!submitting}
      ariaLabel={t('games.questions.suggest.title')}
    >
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('games.questions.suggest.title')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground disabled:opacity-40"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        <Field label={t('games.questions.suggest.category')}>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 rounded-md border-2 border-border bg-card text-sm text-foreground"
            dir="auto"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {t(`games.quiz.deck.${cat}.name`)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('games.questions.suggest.questionEn')}>
          <input
            type="text"
            value={textEn}
            onChange={(e) => setTextEn(e.target.value)}
            placeholder={t('games.questions.suggest.questionEnPh')}
            className="w-full px-3 py-2 rounded-md border-2 border-border bg-card text-sm text-foreground"
            dir="ltr"
          />
        </Field>

        <Field label={t('games.questions.suggest.questionAr')}>
          <input
            type="text"
            value={textAr}
            onChange={(e) => setTextAr(e.target.value)}
            placeholder={t('games.questions.suggest.questionArPh')}
            className="w-full px-3 py-2 rounded-md border-2 border-border bg-card text-sm text-foreground"
            dir="rtl"
          />
        </Field>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">
            {t('games.questions.suggest.options')}
          </p>
          {options.map((opt, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-muted-foreground w-4" dir="ltr">{i + 1}.</span>
                <input
                  type="text"
                  value={opt.en}
                  onChange={(e) => setOpt(i, 'en', e.target.value)}
                  placeholder="EN"
                  className="flex-1 px-3 py-1.5 rounded-md border border-border bg-card text-sm text-foreground"
                  dir="ltr"
                />
                <input
                  type="text"
                  value={opt.ar}
                  onChange={(e) => setOpt(i, 'ar', e.target.value)}
                  placeholder="AR"
                  className="flex-1 px-3 py-1.5 rounded-md border border-border bg-card text-sm text-foreground"
                  dir="rtl"
                />
                {options.length > 3 && (
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    aria-label={t('games.questions.suggest.removeOption')}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {options.length < 5 && (
            <button
              type="button"
              onClick={addOption}
              className="flex items-center gap-1 text-xs text-app-primary-500 hover:underline"
              dir="auto"
            >
              <Plus className="w-3 h-3" />
              {t('games.questions.suggest.addOption')}
            </button>
          )}
        </div>

        {error && <p className="text-xs text-red-500 text-center" dir="auto">{error}</p>}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full mt-4 py-2 rounded-md bg-app-primary-500 text-white font-semibold disabled:opacity-40"
      >
        {submitting ? t('games.questions.suggest.submitting') : t('games.questions.suggest.submit')}
      </button>
    </Modal>
  )
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{label}</p>
      {children}
    </div>
  )
}

function HowToPlayDialog({ onClose, t }) {
  return (
    <Modal open onClose={onClose} ariaLabel={t('games.questions.howToPlay')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('games.questions.howToPlay')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <ul className="space-y-2 text-sm text-foreground" dir="auto">
        <li>{t('games.questions.howTo.line1')}</li>
        <li>{t('games.questions.howTo.line2')}</li>
        <li>{t('games.questions.howTo.line3')}</li>
        <li>{t('games.questions.howTo.line4')}</li>
      </ul>
      <button
        type="button"
        onClick={onClose}
        className="w-full mt-4 py-2 rounded-md bg-app-primary-500 text-white font-semibold"
      >
        {t('games.questions.howTo.cta')}
      </button>
    </Modal>
  )
}
